import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/toaster';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import FloatingCallButton from './components/FloatingCallButton';
import PublicErrorBoundary from './components/PublicErrorBoundary';
import ChatWidget from './components/ChatWidget';

import Home from './pages/Home';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
// Service / narrative pages are rendered by the generic ServicePageView /
// LegalPage components below — their bespoke components live in
// src/pages/{BusinessVoIP,SipTrunk,...}.jsx and are kept on disk as
// reference but no longer routed.

// Admin
import { AuthProvider } from './context/AuthContext';
import { I18nProvider } from './context/I18nContext';
// Unified admin surface — was admin-v2, now THE admin. All admin files live
// in src/pages/admin/ now.
import AdminLayout from './pages/admin/AdminLayout';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminPages from './pages/admin/AdminPages';
import AdminPageEditor from './pages/admin/AdminPageEditor';
import AdminSeoEditor from './pages/admin/AdminSeoEditor';
import {
  AdminV2SeoHub as AdminSeoHub,
  AdminV2AiPrompts as AdminAiPrompts,
} from './pages/admin/Placeholders';
import AdminMediaLibrary from './pages/admin/AdminMedia';
// Legacy CRUD pages — still here, now nested under the unified admin layout
import AdminLeads from './pages/admin/AdminLeads';
import AdminPartners from './pages/admin/AdminPartners';
import AdminOrders from './pages/admin/AdminOrders';
import AdminBlog from './pages/admin/AdminBlog';
import AdminReviews from './pages/admin/AdminReviews';
import AdminFaqs from './pages/admin/AdminFaqs';
import AdminLogos from './pages/admin/AdminLogos';
import AdminSettings from './pages/admin/AdminSettings';
import AdminAuditLog from './pages/admin/AdminAuditLog';
import AdminUsers from './pages/admin/AdminUsers';
import AdminForms from './pages/admin/AdminForms';
import AdminVirtualPricing from './pages/admin/AdminVirtualPricing';
import AdminRegulations from './pages/admin/AdminRegulations';
import AdminContent from './pages/admin/AdminContent';
import AdminResources from './pages/admin/AdminResources';
import { ResourcesIndex, ResourceDetail } from './pages/Resources';
import DesignPreview from './pages/DesignPreview';
import LegalPage from './pages/LegalPage';
import ServicePageView from './pages/ServicePageView';
import VirtualNumbers from './pages/VirtualNumbers';
import BusinessVoIP from './pages/BusinessVoIP';
import PhoneFeatures from './pages/PhoneFeatures';
import MicrosoftTeams from './pages/MicrosoftTeams';
import InboundContactCentre from './pages/InboundContactCentre';
import OutboundContactCentre from './pages/OutboundContactCentre';
import Platform from './pages/Platform';
import VideoConferencing from './pages/VideoConferencing';
import UnifiedComms from './pages/UnifiedComms';
import CrmIntegrations from './pages/CrmIntegrations';
import WebPhone from './pages/WebPhone';
import CallingPlansVsTeamMate from './pages/CallingPlansVsTeamMate';
import Pricing from './pages/Pricing';
import Status from './pages/Status';
import NotFound from './pages/NotFound';
import CookieConsent from './components/CookieConsent';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

function PublicShell({ children }) {
  return (
    <div className="App">
      <Navbar />
      <main>
        <PublicErrorBoundary>{children}</PublicErrorBoundary>
        <CookieConsent />
      </main>
      <Footer />
      <FloatingCallButton />
      <ChatWidget />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
      <AuthProvider>
        <ScrollToTop />
        <Routes>
          {/* Admin — unified surface (v2 chrome + legacy CRUD nested) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            {/* Overview / CMS (new) */}
            <Route index element={<AdminDashboard />} />
            <Route path="pages" element={<AdminPages />} />
            <Route path="pages/:slug" element={<AdminPageEditor />} />
            <Route path="seo" element={<AdminSeoHub />} />
            <Route path="seo/:slug" element={<AdminSeoEditor />} />
            <Route path="ai" element={<AdminAiPrompts />} />
            <Route path="media" element={<AdminMediaLibrary />} />

            {/* Content CRUD (legacy) */}
            <Route path="blog" element={<AdminBlog />} />
            <Route path="resources" element={<AdminResources />} />
            <Route path="faqs" element={<AdminFaqs />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="logos" element={<AdminLogos />} />

            {/* Master data (legacy) */}
            <Route path="virtual-pricing" element={<AdminVirtualPricing />} />
            <Route path="regulations" element={<AdminRegulations />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="audit-log" element={<AdminAuditLog />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="forms" element={<AdminForms />} />

            {/* Inbox (legacy) */}
            <Route path="leads" element={<AdminLeads />} />
            <Route path="partners" element={<AdminPartners />} />
            <Route path="orders" element={<AdminOrders />} />
          </Route>

          {/* Public site */}
          <Route path="/" element={<PublicShell><Home /></PublicShell>} />

          {/* Service / product pages — all served by the generic
              admin-driven ServicePageView. Edit at /admin-v2/pages/<slug>. */}
          {/* Business Phone (Cloud Hosted PBX) keeps its bespoke pricing /
              number-porting / benefits layout. URL stays /business-voip
              for SEO and existing inbound links. */}
          <Route path="/business-voip" element={<PublicShell><BusinessVoIP /></PublicShell>} />
          <Route path="/sip-trunk" element={<PublicShell><ServicePageView slug="sip-trunk" /></PublicShell>} />
          {/* Microsoft Teams keeps its bespoke TeamMate / Direct Routing
              page (architecture diagram + Calling Plans comparison). */}
          <Route path="/microsoft-teams" element={<PublicShell><MicrosoftTeams /></PublicShell>} />
          {/* Contact-centre pages keep their bespoke layouts (5 queue
              types, KPI cards, Sentiment Analysis showcase). */}
          <Route path="/inbound-contact-centre" element={<PublicShell><InboundContactCentre /></PublicShell>} />
          <Route path="/outbound-contact-centre" element={<PublicShell><OutboundContactCentre /></PublicShell>} />
          {/* Collaboration / softphone — bespoke layouts with session
              types, device matrix and channel grid. */}
          <Route path="/web-phone" element={<PublicShell><WebPhone /></PublicShell>} />
          <Route path="/video-conferencing" element={<PublicShell><VideoConferencing /></PublicShell>} />
          <Route path="/unified-communications" element={<PublicShell><UnifiedComms /></PublicShell>} />
          {/* Virtual Numbers keeps its bespoke pricing-table layout — fed
              from admin's virtual_pricing collection via /api/public/virtual-pricing
              (falls back to the legacy mock if the API is empty). */}
          <Route path="/virtual-numbers" element={<PublicShell><VirtualNumbers /></PublicShell>} />
          <Route path="/crm-integrations" element={<PublicShell><CrmIntegrations /></PublicShell>} />
          {/* Phone Features keeps its bespoke 50+ filterable feature grid +
              9-card "headline features" spotlight from the brochure. */}
          <Route path="/phone-features" element={<PublicShell><PhoneFeatures /></PublicShell>} />
          <Route path="/white-label" element={<PublicShell><ServicePageView slug="white-label" /></PublicShell>} />
          <Route path="/wholesale-voip" element={<PublicShell><ServicePageView slug="wholesale-voip" /></PublicShell>} />
          <Route path="/managed-infrastructure" element={<PublicShell><ServicePageView slug="managed-infrastructure" /></PublicShell>} />

          {/* /platform — infrastructure / network / trust page. Aimed at
              IT / security / procurement buyers; sourced from the platform
              feature brochure. */}
          <Route path="/platform" element={<PublicShell><Platform /></PublicShell>} />

          {/* /microsoft-calling-plans-vs-teammate — bottom-of-funnel
              comparison page with TCO calculator. Targets buying-intent
              keywords ("Calling Plans alternative", "Direct Routing UK"). */}
          <Route path="/microsoft-calling-plans-vs-teammate" element={<PublicShell><CallingPlansVsTeamMate /></PublicShell>} />

          {/* Unified pricing page + live status. Both feed into navbar
              promotion later but render correctly today even when
              linked from external sources. */}
          <Route path="/pricing" element={<PublicShell><Pricing /></PublicShell>} />
          <Route path="/status" element={<PublicShell><Status /></PublicShell>} />

          {/* Narrative pages — same LegalPage renderer accepts rich_text
              and `legal` section types so About/Awards/Reviews/Contact all
              read straight from /admin-v2/pages/<slug>. */}
          <Route path="/about" element={<PublicShell><LegalPage slug="about" /></PublicShell>} />
          <Route path="/awards" element={<PublicShell><LegalPage slug="awards" /></PublicShell>} />
          <Route path="/reviews" element={<PublicShell><LegalPage slug="reviews" /></PublicShell>} />

          {/* Form-driven pages keep their custom components for now */}
          <Route path="/contact" element={<PublicShell><Contact /></PublicShell>} />
          <Route path="/blog" element={<PublicShell><Blog /></PublicShell>} />
          <Route path="/blog/:slug" element={<PublicShell><BlogPost /></PublicShell>} />
          <Route path="/resources" element={<PublicShell><ResourcesIndex /></PublicShell>} />
          <Route path="/resources/:slug" element={<PublicShell><ResourceDetail /></PublicShell>} />

          <Route path="/design-preview" element={<DesignPreview />} />

          {/* Legal / static pages (body editable in /admin-v2/pages/<slug>) */}
          <Route path="/privacy-policy" element={<PublicShell><LegalPage slug="privacy-policy" /></PublicShell>} />
          <Route path="/terms-conditions" element={<PublicShell><LegalPage slug="terms-conditions" /></PublicShell>} />
          <Route path="/cookies-policy" element={<PublicShell><LegalPage slug="cookies-policy" /></PublicShell>} />
          <Route path="/acceptable-use-policy" element={<PublicShell><LegalPage slug="acceptable-use-policy" /></PublicShell>} />

          {/* Custom 404 — replaces the previous catch-all-to-Home fallback
              which was confusing UX (looked like you'd arrived somewhere). */}
          <Route path="*" element={<PublicShell><NotFound /></PublicShell>} />
        </Routes>
        <Toaster />
      </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
